import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { formatDate } from "../lib/format";
import type { FeedbackItem } from "../lib/types";
import { Badge, EmptyState, ErrorBanner, PageHeader, Panel, Spinner } from "../components/ui";
import { FEEDBACK_KINDS, FEEDBACK_SENT } from "../components/FeedbackButton";

const kindLabel = (kind: FeedbackItem["kind"]) => FEEDBACK_KINDS.find((k) => k.key === kind)?.label ?? kind;
const kindTone = (kind: FeedbackItem["kind"]) => (kind === "PROBLEM" ? "critical" : kind === "RATES" ? "warning" : "info");

/** A phone or a computer, and which browser, from the device string — enough to reproduce on. */
function deviceOf(agent: string | null | undefined): string | null {
  if (!agent) return null;
  const os = /iPhone|iPad/.test(agent) ? "iPhone/iPad" : /Android/.test(agent) ? "Android" : /Mac OS X/.test(agent) ? "Mac" : /Windows/.test(agent) ? "Windows" : null;
  const browser = /Edg\//.test(agent) ? "Edge" : /Chrome\//.test(agent) ? "Chrome" : /Firefox\//.test(agent) ? "Firefox" : /Safari\//.test(agent) ? "Safari" : null;
  return [os, browser].filter(Boolean).join(" · ") || null;
}

export default function FeedbackPage() {
  const { user } = useAuth();
  const [mine, setMine] = useState<FeedbackItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadMine = useCallback(() => {
    api
      .get<{ feedback: FeedbackItem[] }>("/feedback/mine")
      .then((r) => setMine(r.feedback))
      .catch((err) => setError(err instanceof ApiError ? err.message : "Could not load your feedback"));
  }, []);
  useEffect(() => {
    loadMine();
    window.addEventListener(FEEDBACK_SENT, loadMine);
    return () => window.removeEventListener(FEEDBACK_SENT, loadMine);
  }, [loadMine]);

  return (
    <div>
      <PageHeader
        title="Feedback"
        subtitle="Suggest a change from anywhere with the link in the menu. What you've sent, and any reply, is here."
      />
      {error && <ErrorBanner message={error} />}
      <div className="space-y-6">
        {user?.isAdmin && <TestersPanel />}
        {user?.isAdmin && <EveryonesFeedback onChanged={loadMine} />}
        <Panel title="What you've sent">
          {!mine ? (
            <Spinner />
          ) : mine.length === 0 ? (
            <EmptyState title="Nothing yet" hint="Use Suggest a change in the menu — on any page, about anything." />
          ) : (
            <ul className="divide-y divide-ink-100">
              {mine.map((f) => (
                <li key={f.id} className="py-3 first:pt-0 last:pb-0">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-ink-500">
                    <Badge tone={kindTone(f.kind)}>{kindLabel(f.kind)}</Badge>
                    <span>{formatDate(f.createdAt)}</span>
                    {f.status === "DONE" && <Badge tone="good">Done</Badge>}
                  </div>
                  <p className="mt-1.5 whitespace-pre-wrap text-sm text-ink-800">{f.message}</p>
                  {f.reply && (
                    <p className="mt-2 rounded-md bg-accent-50 px-3 py-2 text-sm text-accent-900">
                      <span className="font-medium">Reply: </span>
                      {f.reply}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}

/** Everyone's feedback, for the owner: mark it done and say what was done. */
function EveryonesFeedback({ onChanged }: { onChanged: () => void }) {
  const [status, setStatus] = useState<"NEW" | "DONE" | "ALL">("NEW");
  const [data, setData] = useState<{ feedback: FeedbackItem[]; counts: { new: number; done: number } } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.get(`/feedback?status=${status}`));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load feedback");
    }
  }, [status]);
  useEffect(() => {
    const reload = () => void load();
    reload();
    window.addEventListener(FEEDBACK_SENT, reload);
    return () => window.removeEventListener(FEEDBACK_SENT, reload);
  }, [load]);

  async function update(id: string, patch: { status?: "NEW" | "DONE"; reply?: string | null }) {
    try {
      await api.put(`/feedback/${id}`, patch);
      await load();
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save that");
    }
  }

  return (
    <Panel
      title="Everyone's feedback"
      subtitle="Only you see this list. Replies show to the person who sent it."
      actions={
        <div className="flex gap-1 text-xs">
          {(["NEW", "DONE", "ALL"] as const).map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`rounded-md px-2.5 py-1 ${status === s ? "bg-accent-600 text-white" : "bg-ink-100 text-ink-700 hover:bg-ink-200"}`}
            >
              {s === "NEW" ? `New${data ? ` (${data.counts.new})` : ""}` : s === "DONE" ? `Done${data ? ` (${data.counts.done})` : ""}` : "All"}
            </button>
          ))}
        </div>
      }
    >
      {error && <ErrorBanner message={error} />}
      {!data ? (
        <Spinner />
      ) : data.feedback.length === 0 ? (
        <EmptyState title={status === "NEW" ? "Nothing new" : "Nothing here"} />
      ) : (
        <ul className="divide-y divide-ink-100">
          {data.feedback.map((f) => (
            <FeedbackRow key={f.id} item={f} onUpdate={(patch) => update(f.id, patch)} />
          ))}
        </ul>
      )}
    </Panel>
  );
}

function FeedbackRow({ item, onUpdate }: { item: FeedbackItem; onUpdate: (patch: { status?: "NEW" | "DONE"; reply?: string | null }) => void }) {
  const [reply, setReply] = useState(item.reply ?? "");
  const device = deviceOf(item.device);
  return (
    <li className="py-4 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-center gap-2 text-xs text-ink-500">
        <span className="font-medium text-ink-900">{item.from}</span>
        <Badge tone={kindTone(item.kind)}>{kindLabel(item.kind)}</Badge>
        <span>{formatDate(item.createdAt)}</span>
        {item.page && (
          <Link to={item.page} className="underline">
            {item.page}
          </Link>
        )}
        {item.version && <span>v{item.version}</span>}
        {device && <span>{device}</span>}
      </div>
      <p className="mt-1.5 whitespace-pre-wrap text-sm text-ink-800">{item.message}</p>
      <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-start">
        <textarea
          aria-label="Reply"
          className="input min-h-[2.5rem] flex-1 text-sm"
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          placeholder="Reply — what you did, or why not (optional)"
        />
        <div className="flex shrink-0 gap-2">
          {reply.trim() !== (item.reply ?? "") && (
            <button className="btn-secondary px-3 py-1.5 text-xs" onClick={() => onUpdate({ reply: reply.trim() || null })}>
              Save reply
            </button>
          )}
          <button
            className={`${item.status === "DONE" ? "btn-secondary" : "btn-primary"} px-3 py-1.5 text-xs`}
            onClick={() => onUpdate({ status: item.status === "DONE" ? "NEW" : "DONE", ...(reply.trim() !== (item.reply ?? "") ? { reply: reply.trim() || null } : {}) })}
          >
            {item.status === "DONE" ? "Reopen" : "Mark done"}
          </button>
        </div>
      </div>
    </li>
  );
}

interface Tester {
  id: string;
  name: string | null;
  login: string;
  isAdmin: boolean;
  passwordSet: boolean;
  createdAt: string;
  lastSeenAt: string | null;
  cards: number;
  spends: number;
  feedback: number;
}

/** "today", "yesterday", "5 days ago" — how long since a date, in whole days. */
function daysAgo(iso: string): string {
  const start = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((start(new Date()) - start(new Date(iso))) / 86400000);
  return days <= 0 ? "today" : days === 1 ? "yesterday" : `${days} days ago`;
}

/**
 * Who has tried the app, for the owner: last opened, password set, and how
 * much each has added — counts only. Last-opened dates start from 28 Sep 2026.
 */
function TestersPanel() {
  const [testers, setTesters] = useState<Tester[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ testers: Tester[] }>("/feedback/testers")
      .then((r) => setTesters(r.testers.filter((t) => !t.isAdmin)))
      .catch((err) => setError(err instanceof ApiError ? err.message : "Could not load testers"));
  }, []);

  if (error) return <ErrorBanner message={error} />;
  if (!testers) return <Spinner />;

  const signedIn = testers.filter((t) => t.passwordSet).length;
  const weekAgo = Date.now() - 7 * 86400000;
  const thisWeek = testers.filter((t) => t.lastSeenAt && new Date(t.lastSeenAt).getTime() >= weekAgo).length;
  // Most recently active first; those who never signed in last.
  const sorted = [...testers].sort(
    (a, b) =>
      Number(b.passwordSet) - Number(a.passwordSet) ||
      (b.lastSeenAt ? new Date(b.lastSeenAt).getTime() : 0) - (a.lastSeenAt ? new Date(a.lastSeenAt).getTime() : 0) ||
      (a.name ?? a.login).localeCompare(b.name ?? b.login),
  );

  return (
    <Panel
      title="Testers"
      subtitle={`${signedIn} of ${testers.length} have signed in · ${thisWeek} opened the app in the last 7 days. Counts only — never their cards or spends.`}
    >
      <div className="-mx-4 overflow-x-auto px-4">
        <table className="w-full min-w-[34rem] text-sm">
          <thead>
            <tr className="text-left text-xs text-ink-500">
              <th className="py-1.5 pr-3 font-medium">Tester</th>
              <th className="py-1.5 pr-3 font-medium">Last opened</th>
              <th className="py-1.5 pr-3 text-right font-medium">Cards</th>
              <th className="py-1.5 pr-3 text-right font-medium">Spends</th>
              <th className="py-1.5 text-right font-medium">Feedback</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {sorted.map((t) => (
              <tr key={t.id}>
                <td className="py-2 pr-3">
                  <span className="font-medium text-ink-900">{t.name ?? t.login}</span>
                  {t.name && <span className="block text-xs text-ink-500">{t.login}</span>}
                </td>
                <td className="py-2 pr-3 text-ink-700">
                  {!t.passwordSet ? (
                    <span className="text-ink-400">Not signed in yet</span>
                  ) : t.lastSeenAt ? (
                    daysAgo(t.lastSeenAt)
                  ) : (
                    <span className="text-ink-500">Before 28 Sep</span>
                  )}
                </td>
                <td className="tnum py-2 pr-3 text-right text-ink-800">{t.cards || "—"}</td>
                <td className="tnum py-2 pr-3 text-right text-ink-800">{t.spends || "—"}</td>
                <td className="tnum py-2 text-right text-ink-800">{t.feedback || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}
